import { YearEndStatements } from '../../components/admin/YearEndStatements';
import React, { useEffect, useState } from 'react';
import { Loader2, HeartHandshake, Download, Plus, Trash2, Save } from 'lucide-react';
import api, { errText } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const ICON_OPTIONS = ['HandHeart', 'BookOpen', 'Home', 'Globe', 'Laptop', 'Flame', 'Heart'];

const FundsManager = () => {
  const { toast } = useToast();
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ name: '', description: '', icon: 'HandHeart' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/admin/donations/funds'); setFunds(r.data || []); }
    catch (e) { toast({ title: 'Failed to load purposes', description: errText(e) }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (!draft.name.trim()) return toast({ title: 'Name is required' });
    setSaving(true);
    try {
      await api.post('/admin/donations/funds', { ...draft, sort_order: funds.length });
      setDraft({ name: '', description: '', icon: 'HandHeart' });
      toast({ title: 'Purpose added 🌸' }); load();
    } catch (e) { toast({ title: 'Could not add', description: errText(e) }); }
    finally { setSaving(false); }
  };

  const toggleActive = async (f) => {
    try { await api.patch(`/admin/donations/funds/${f.id}`, { ...f, is_active: !f.is_active }); load(); }
    catch (e) { toast({ title: 'Update failed', description: errText(e) }); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Remove donation purpose "${f.name}"?`)) return;
    try { await api.delete(`/admin/donations/funds/${f.id}`); toast({ title: 'Removed' }); load(); }
    catch (e) { toast({ title: 'Delete failed', description: errText(e) }); }
  };

  return (
    <div className="rounded-2xl border border-rose/50 bg-white/70 p-5" data-testid="funds-manager">
      <h2 className="font-serif-display text-2xl text-burgundy mb-1">Donation Purposes</h2>
      <p className="text-sm text-mahogany/70 mb-4">These are the funds people can choose on the Donate page. Add, hide or remove them anytime.</p>

      {loading ? <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div> : (
        <div className="space-y-2 mb-5">
          {funds.map((f) => (
            <div key={f.id} className={`flex items-center gap-3 p-3 rounded-xl border ${f.is_active ? 'border-rose/40 bg-white' : 'border-rose/30 bg-rose-soft/20 opacity-70'}`} data-testid={`fund-row-${f.id}`}>
              <div className="flex-1">
                <div className="font-medium text-burgundy">{f.name} {!f.is_active && <span className="text-xs text-mahogany/50">(hidden)</span>}</div>
                {f.description && <div className="text-xs text-mahogany/60 line-clamp-1">{f.description}</div>}
              </div>
              <button onClick={() => toggleActive(f)} className="text-xs pill-btn-outline py-1" data-testid={`fund-toggle-${f.id}`}>{f.is_active ? 'Hide' : 'Show'}</button>
              <button onClick={() => remove(f)} className="p-2 hover:bg-red-50 rounded-lg" data-testid={`fund-delete-${f.id}`}><Trash2 className="w-4 h-4 text-red-600" /></button>
            </div>
          ))}
        </div>
      )}

      <div className="border-t border-rose/30 pt-4 grid sm:grid-cols-[1fr_1fr_140px_auto] gap-2 items-end">
        <label className="block"><span className="block text-xs uppercase tracking-widest text-mahogany/70 mb-1">New purpose name</span>
          <input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} className="w-full bg-white border border-rose/50 rounded-lg px-3 py-2 text-burgundy" data-testid="fund-name-input" /></label>
        <label className="block"><span className="block text-xs uppercase tracking-widest text-mahogany/70 mb-1">Description</span>
          <input value={draft.description} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} className="w-full bg-white border border-rose/50 rounded-lg px-3 py-2 text-burgundy" data-testid="fund-desc-input" /></label>
        <label className="block"><span className="block text-xs uppercase tracking-widest text-mahogany/70 mb-1">Icon</span>
          <select value={draft.icon} onChange={(e) => setDraft((d) => ({ ...d, icon: e.target.value }))} className="w-full bg-white border border-rose/50 rounded-lg px-3 py-2 text-burgundy" data-testid="fund-icon-select">
            {ICON_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
          </select></label>
        <button onClick={add} disabled={saving} className="pill-btn" data-testid="fund-add-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add</button>
      </div>
    </div>
  );
};

const fmtDate = (d) => d ? new Date(d).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';
const Badge = ({ status }) => {
  const map = { paid: 'bg-green-100 text-green-800', pending: 'bg-amber-100 text-amber-800', failed: 'bg-red-100 text-red-700' };
  return <span className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${map[status] || 'bg-rose-soft text-mahogany'}`}>{status || 'pending'}</span>;
};

const AdminDonations = () => {
  const [data, setData] = useState({ donations: [], total_raised: 0, paid_count: 0, total_count: 0 });
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try { const r = await api.get('/admin/donations'); setData(r.data); } catch (e) { console.error('Failed to load donations:', e); } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const exportCsv = () => {
    const rows = [['Date', 'Donor', 'Email', 'Fund', 'Amount', 'Status']];
    data.donations.forEach((d) => rows.push([fmtDate(d.created_at), d.donor_name || '', d.donor_email || '', d.fund || '', d.amount, d.payment_status || '']));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = 'donations.csv'; a.click(); URL.revokeObjectURL(a.href);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-2">Admin</div>
          <h1 className="font-serif-display text-4xl text-burgundy">Donations</h1>
        </div>
        {data.donations.length > 0 && <button onClick={exportCsv} className="pill-btn-outline" data-testid="donations-export"><Download className="w-4 h-4" /> Export CSV</button>}
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="p-5 rounded-2xl bg-white/80 border border-rose/50" data-testid="donations-total">
          <div className="flex items-center gap-2 text-mahogany/70 text-sm"><HeartHandshake className="w-4 h-4" /> Total Raised</div>
          <div className="mt-1 font-serif-display text-3xl text-burgundy">${data.total_raised?.toFixed(2)}</div>
        </div>
        <div className="p-5 rounded-2xl bg-white/80 border border-rose/50">
          <div className="text-mahogany/70 text-sm">Completed Donations</div>
          <div className="mt-1 font-serif-display text-3xl text-burgundy">{data.paid_count}</div>
        </div>
        <div className="p-5 rounded-2xl bg-white/80 border border-rose/50">
          <div className="text-mahogany/70 text-sm">Total Attempts</div>
          <div className="mt-1 font-serif-display text-3xl text-burgundy">{data.total_count}</div>
        </div>
      </div>

      <FundsManager />

      {loading ? <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        : data.donations.length === 0 ? <div className="p-8 text-center text-mahogany/60 bg-white/60 rounded-2xl border border-rose/40" data-testid="donations-empty">No donations yet.</div>
        : (
          <div className="overflow-x-auto rounded-2xl border border-rose/50 bg-white/70" data-testid="donations-table">
            <table className="w-full text-sm">
              <thead className="text-left text-mahogany/70 border-b border-rose/40">
                <tr><th className="p-3">Date</th><th className="p-3">Donor</th><th className="p-3">Fund</th><th className="p-3">Amount</th><th className="p-3">Status</th><th className="p-3">Receipt</th></tr>
              </thead>
              <tbody>
                {data.donations.map((d) => (
                  <tr key={d.session_id} className="border-b border-rose/20 last:border-0" data-testid={`donation-row-${d.session_id}`}>
                    <td className="p-3 text-mahogany/80 whitespace-nowrap">{fmtDate(d.created_at)}</td>
                    <td className="p-3 text-burgundy">{d.donor_name || 'Anonymous'}<div className="text-xs text-mahogany/50">{d.donor_email}</div></td>
                    <td className="p-3 text-burgundy/85">{d.fund}</td>
                    <td className="p-3 font-medium text-burgundy">${Number(d.amount).toFixed(2)}</td>
                    <td className="p-3"><Badge status={d.payment_status} /></td>
                    <td className="p-3">{d.payment_status === 'paid' && <a href={`${process.env.REACT_APP_BACKEND_URL}/api/donations/${d.session_id}/receipt`} target="_blank" rel="noopener noreferrer" className="text-rose-italic underline text-xs" data-testid={`receipt-${d.session_id}`}>Receipt</a>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      <YearEndStatements />
    </div>
  );
};

export default AdminDonations;
